import React, { useState, useEffect } from 'react';
import { adminService } from '../../services/adminService';
import { AdminSessionMessage, AdminSessionMessagesResponse } from '../../types/admin';
import { MessageCircle, User, Bot, Clock, AlertCircle, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';

interface SessionMessagesProps {
  sessionId: string | null;
}

// Helper function to extract text content from structured fullContent
const extractTextContent = (fullContent: any): string => {
  // Handle null/undefined
  if (!fullContent) return '';
  
  // If it's already a string, return it
  if (typeof fullContent === 'string') {
    return fullContent;
  }
  
  // Handle array of content objects
  if (Array.isArray(fullContent)) {
    return fullContent
      .map(item => {
        if (typeof item === 'string') return item;
        if (item?.text?.value) return item.text.value;
        if (item?.text) return item.text;
        return '';
      })
      .join(' ');
  }
  
  // Handle single content object
  if (fullContent?.text?.value) {
    return fullContent.text.value;
  }
  
  // Handle direct text property
  if (fullContent?.text) {
    return typeof fullContent.text === 'string' ? fullContent.text : '';
  }
  
  // Fallback to string conversion
  if (typeof fullContent === 'object') {
    return JSON.stringify(fullContent);
  }
  
  return String(fullContent);
};

export const SessionMessages: React.FC<SessionMessagesProps> = ({ sessionId }) => {
  const [messages, setMessages] = useState<AdminSessionMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionInfo, setSessionInfo] = useState<any>(null);
  const [pagination, setPagination] = useState<{
    hasMore: boolean;
    nextCursor?: string;
  } | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isCopied, setIsCopied] = useState(false);

  const copyMessagesToClipboard = async () => {
    if (messages.length === 0) return;

    const formattedMessages = messages
      .map(message => {
        const role = message.role === 'user' ? 'User' : 'Assistant';
        const content = extractTextContent(message.fullContent) || message.content;
        return `${role}: ${content}`;
      })
      .join('\n\n---\n\n');

    try {
      await navigator.clipboard.writeText(formattedMessages);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy messages:', err);
    }
  };

  useEffect(() => {
    if (!sessionId) {
      setMessages([]);
      setSessionInfo(null);
      setPagination(null);
      setError(null);
      return;
    }

    const loadSessionMessages = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Load first 50 messages by default
        const response: AdminSessionMessagesResponse = await adminService.getSessionMessages(sessionId, {
          limit: 50,
          order: 'asc'
        });
        setMessages(response.messages || []);
        setSessionInfo(response.sessionInfo);
        setPagination({
          hasMore: response.pagination?.hasMore || response.hasMore || false,
          nextCursor: response.pagination?.nextCursor
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load session messages');
        setMessages([]);
        setSessionInfo(null);
        setPagination(null);
      } finally {
        setLoading(false);
      }
    };

    loadSessionMessages();
  }, [sessionId]);

  const loadMoreMessages = async () => {
    if (!sessionId || !pagination?.nextCursor || loadingMore) {
      return;
    }

    setLoadingMore(true);
    setError(null);
    
    try {
      const response: AdminSessionMessagesResponse = await adminService.getSessionMessages(sessionId, {
        limit: 10,
        order: 'asc',
        after: pagination.nextCursor
      });
      
      // Append new messages to existing ones
      setMessages(prev => [...prev, ...(response.messages || [])]);
      setPagination({
        hasMore: response.pagination?.hasMore || response.hasMore || false,
        nextCursor: response.pagination?.nextCursor
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more messages');
    } finally {
      setLoadingMore(false);
    }
  };

  if (!sessionId) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <MessageCircle className="w-5 h-5 mr-2" />
            Session Messages
          </h2>
          <button 
            disabled
            className="p-2 rounded-lg transition-colors opacity-50"
            aria-label="Loading..."
          >
            <ChevronDown className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Loading messages...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <MessageCircle className="w-5 h-5 mr-2" />
            Session Messages
          </h2>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={isExpanded ? 'Collapse messages' : 'Expand messages'}
          >
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>
        {isExpanded && (
          <div className="flex items-center p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 mr-3" />
            <span className="text-red-700">{error}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <MessageCircle className="w-5 h-5 mr-2" />
          Session Messages
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({messages.length}{pagination?.hasMore ? '+' : ''} message{messages.length !== 1 ? 's' : ''})
          </span>
        </h2>
        <div className="flex items-center space-x-2">
          {messages.length > 0 && (
            <button
              onClick={copyMessagesToClipboard}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center"
              aria-label="Copy all messages to clipboard"
              title="Copy all messages to clipboard"
            >
              {isCopied ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <Copy className="w-5 h-5 text-gray-500" />
              )}
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={isExpanded ? 'Collapse messages' : 'Expand messages'}
          >
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <>
          {sessionInfo && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Session Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Thread ID:</span>
                  <span className="ml-2 font-mono">{sessionInfo.threadId || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Type:</span>
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                    sessionInfo.sessionType === 'foundry'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {sessionInfo.sessionType}
                  </span>
                </div>
              </div>
            </div>
          )}

          {messages.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No messages found for this session</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex items-start space-x-3 p-4 rounded-lg ${
                    message.role === 'user'
                      ? 'bg-blue-50 border border-blue-100'
                      : 'bg-gray-50 border border-gray-100'
                  }`}
                >
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.role === 'user'
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {message.role === 'user' ? (
                      <User className="w-4 h-4" />
                    ) : (
                      <Bot className="w-4 h-4" />
                    )}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className={`text-sm font-medium ${
                        message.role === 'user' ? 'text-blue-900' : 'text-gray-900'
                      }`}>
                        {message.role === 'user' ? 'User' : 'Assistant'}
                      </span>
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="w-3 h-3 mr-1" />
                        {new Date(message.timestamp).toLocaleString()}
                      </div>
                    </div>
                    
                    <div className="text-sm text-gray-700">
                      <div className="whitespace-pre-wrap break-words">
                        {extractTextContent(message.fullContent) || message.content}
                      </div>
                    </div>

                    {message.metadata && (
                      <div className="mt-2 text-xs text-gray-400">
                        <span className="font-mono">ID: {message.metadata.foundryMessageId || message.id}</span>
                        {message.metadata.assistantId && (
                          <span className="ml-3 font-mono">Assistant: {message.metadata.assistantId}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Load More Button */}
          {pagination?.hasMore && (
            <div className="mt-4 text-center">
              <button
                onClick={loadMoreMessages}
                disabled={loadingMore}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  loadingMore
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2'
                }`}
              >
                {loadingMore ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
                    Loading more...
                  </div>
                ) : (
                  `Load More Messages (${messages.length} shown)`
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};